import type { PricingTier } from '../services/planPricingService';
import { PRO_TIER_CAPS } from './proEmailLimits';

export type ContactTierOption = { label: string; value: number };

function sortedUniqueCaps(tiers: PricingTier[]): number[] {
  if (!tiers?.length) {
    return [...PRO_TIER_CAPS];
  }
  return Array.from(
    new Set(tiers.map((t) => t.subscribers).filter((n) => Number.isFinite(n) && n > 0))
  ).sort((a, b) => a - b);
}

/**
 * Dropdown options: free bucket, then one row per paid tier cap (labels show inclusive ranges).
 * `value` is always a tier cap (or 100001 = above max → checkout capped at 100k) so UI, `calculatePlanPrice`, and Dodo `tier_cap` stay aligned.
 */
export function buildContactTierOptions(tiers: PricingTier[]): ContactTierOption[] {
  const caps = sortedUniqueCaps(tiers);
  const out: ContactTierOption[] = [{ label: '0 - 1,000 (free plan)', value: 1000 }];
  let prev = 1000;
  for (const cap of caps) {
    if (cap <= 1000) continue;
    const low = prev + 1;
    out.push({
      label: `${low.toLocaleString()} - ${cap.toLocaleString()}`,
      value: cap,
    });
    prev = cap;
  }
  out.push({ label: '100,000+ (Contact Sales)', value: 100001 });
  return out;
}

/** Map stored subscriber count to the `<select>` option value (nearest tier cap upward, except free and 100k+). */
export function subscribersToSelectValue(subscribers: number, tiers: PricingTier[]): number {
  if (!Number.isFinite(subscribers) || subscribers <= 1000) return 1000;
  if (subscribers >= 100000) return 100001;
  const caps = sortedUniqueCaps(tiers).filter((c) => c > 1000);
  const tier = caps.find((c) => subscribers <= c) ?? caps[caps.length - 1];
  return tier;
}
